import { useEffect, useState } from "react";
import { apiClient, ApiClientError } from "../../lib/api-client";
import { env } from "../../lib/env";
import { useAuthStore } from "../../stores/auth-store";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Field, Input, Textarea } from "../ui/Input";
import { Skeleton } from "../ui/Skeleton";
import LetterLinear from "~icons/solar/letter-linear";
import MagicStick3Linear from "~icons/solar/magic-stick-3-linear";
import CheckCircleLinear from "~icons/solar/check-circle-linear";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ModalState =
  | "idle"
  | "loading_ai"
  | "ready"
  | "ai_error"
  | "sending"
  | "sent"
  | "sent_error";

export interface GreetingPreviewModalProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  candidateEmail?: string;
  projectId: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

// ---------------------------------------------------------------------------
// GreetingPreviewModal
// ---------------------------------------------------------------------------

export function GreetingPreviewModal({
  open,
  onClose,
  candidateId,
  candidateName,
  candidateEmail,
  projectId,
  onSuccess,
  onError: _onError,
}: GreetingPreviewModalProps) {
  const { user } = useAuthStore.getState();

  const [state, setState] = useState<ModalState>("idle");
  const [subject, setSubject] = useState("Exciting Opportunity");
  const [emailContent, setEmailContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Trigger AI generation when the modal opens; reset when it closes
  useEffect(() => {
    if (open) {
      void generateGreeting();
    } else {
      setState("idle");
      setSubject("Exciting Opportunity");
      setEmailContent("");
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function generateGreeting() {
    setState("loading_ai");
    setError(null);
    try {
      const response = await fetch(
        `${env.AI_API_URL}/api/ai/v1/tools/create-email-greeting`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
          },
          body: JSON.stringify({ candidate_id: candidateId, project_id: projectId }),
        }
      );
      const data = (await response.json()) as {
        success?: boolean;
        data?: { email_content?: string };
      };
      if (data.success && data.data?.email_content) {
        setEmailContent(data.data.email_content);
        setSubject("Exciting Opportunity");
        setState("ready");
      } else {
        setState("ai_error");
        setError("Failed to generate greeting. You can write your own message.");
      }
    } catch {
      setState("ai_error");
      setError("Failed to generate greeting. You can write your own message.");
    }
  }

  async function handleSend() {
    if (state === "loading_ai" || state === "sending") return;
    setState("sending");
    setError(null);
    try {
      await apiClient.post(`/candidates/${candidateId}/greeting`, {
        type: "email",
        project_id: projectId,
        subject,
        message: emailContent,
      });
      // v3: brief "Email Sent Successfully!" panel, then close.
      setState("sent");
      setTimeout(() => {
        onSuccess(`Greeting email sent to ${candidateName}`);
        onClose();
      }, 1200);
    } catch (err) {
      const message =
        err instanceof ApiClientError && err.errors.length > 0
          ? err.errors[0]
          : "Failed to send greeting email. Please try again.";
      setState("sent_error");
      setError(message);
    }
  }

  const isBusy = state === "loading_ai" || state === "sending";
  const canSend = !isBusy && emailContent.trim().length > 0;
  const isAiLoading = state === "loading_ai";
  const showForm =
    state === "ready" ||
    state === "ai_error" ||
    state === "sending" ||
    state === "sent_error";

  return (
    <Modal
      open={open}
      onClose={() => {
        if (state !== "sending" && state !== "sent") onClose();
      }}
      title="Contact Candidate via Email"
      description={`Drafted by AI for ${candidateName}. Edit before sending.`}
      icon={LetterLinear}
      size="lg"
      footer={
        state === "sent" ? undefined : (
        <>
          <Button variant="secondary" onClick={onClose} disabled={state === "sending"}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSend()}
            disabled={!canSend}
            loading={state === "sending"}
          >
            Send Email
          </Button>
        </>
        )
      }
    >
      {state === "sent" ? (
        <div className="my-8 text-center" role="status">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-status-green-bg text-status-green-text">
            <CheckCircleLinear aria-hidden="true" className="size-6" />
          </div>
          <h3 className="mt-3 text-base font-bold text-ink">Email Sent Successfully!</h3>
          <p className="text-xs text-ink-muted">Candidate recruitment stage has been updated to Contacted.</p>
        </div>
      ) : (
      <div className="flex flex-col gap-5">
        {/* From / To (v3: stacked read-only fields) */}
        <Field label="From">
          {(id) => <Input id={id} type="text" value={user?.email ?? "—"} disabled readOnly />}
        </Field>
        <Field label="To">
          {(id) => <Input id={id} type="text" value={candidateEmail ?? "—"} disabled readOnly />}
        </Field>

        {/* AI drafting */}
        {isAiLoading && (
          <div
            role="status"
            className="flex flex-col gap-3 rounded-3xl border border-black/[0.06] bg-surface-2 p-5"
          >
            <div className="flex items-center gap-2 text-xs font-semibold text-primary">
              <MagicStick3Linear aria-hidden="true" className="size-4 animate-pulse" />
              Drafting a greeting from the candidate's profile…
            </div>
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-11/12" />
            <Skeleton className="h-3.5 w-4/5" />
            <Skeleton className="h-3.5 w-2/3" />
          </div>
        )}

        {/* AI error */}
        {state === "ai_error" && error && (
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-error/20 bg-error-light px-4 py-3">
            <p className="text-xs font-medium text-error">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void generateGreeting()}
              className="shrink-0"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Send error */}
        {state === "sent_error" && error && (
          <p role="alert" className="rounded-2xl border border-error/20 bg-error-light px-4 py-3 text-xs font-medium text-error">
            {error}
          </p>
        )}

        {showForm && (
          <>
            <Field label="Subject">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={isBusy}
                  placeholder="Enter subject"
                />
              )}
            </Field>
            <Field
              label="Message"
              hint="This email was drafted by AI from the candidate's profile. Edit it freely before sending."
            >
              {(id) => (
                <Textarea
                  id={id}
                  rows={10}
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  disabled={isBusy}
                  placeholder="Write your message…"
                />
              )}
            </Field>
          </>
        )}
      </div>
      )}
    </Modal>
  );
}
